import React, { useCallback } from "react"
import { Alert, Button, Collapse, Descriptions, Tag, Row, Col, message, Space, Typography } from "antd"
import { CheckCircleOutlined, CopyOutlined, FileTextOutlined, TableOutlined, UserOutlined } from "@ant-design/icons"
import ApprovalInfo from "@/atoms/approval-info"
import { IApprovalProccessId, ISapReservation } from "@/types"
import ReservationItemsTable from "./ReservationItemsTable"

const { Panel } = Collapse
const { Text } = Typography

interface ISapSanitizedReservation extends Omit<ISapReservation, "approval_process_id"> {
  approval_process_id?: {
    status: number
    data: IApprovalProccessId
  }
}

interface Props {
  data: ISapSanitizedReservation
}

const DetailSection: React.FC<{ title: React.ReactNode; icon: React.ReactNode; children: React.ReactNode; extra?: React.ReactNode }> = ({ title, icon, children, extra }) => {
  return (
    <Collapse defaultActiveKey={["1"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
      <Panel header={<span>{icon} <span style={{ marginLeft: 8 }}>{title}</span></span>} key="1" extra={extra}>
        {children}
      </Panel>
    </Collapse>
  )
}

const SapReservationDetailView: React.FC<Props> = ({ data }) => {
  const getStatusColor = useCallback((status?: string) => {
    switch (status) {
      case "Approved":
        return "green"
      case "Reject":
      case "Cancel":
        return "red"
      case "Waiting Approval":
        return "blue"
      default:
        return "blue"
    }
  }, [])

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      void message.success("Reservation link copied")
    } catch (_error) {
      void message.error("Failed to copy reservation link")
    }
  }, [])

  const latestApprovedTime =
    data.approval_process_id?.data?.detail
      ?.map(item => item?.approved_time)
      .filter((value): value is string => Boolean(value))
      .sort()
      .slice(-1)[0] || null

  return (
    <>
      {String(data.approval_status || "").toUpperCase() === "APPROVED" ? (
        <Alert
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
          style={{ marginBottom: 24 }}
          message="This reservation has been approved"
          description={latestApprovedTime ? `Approved at ${new Date(latestApprovedTime).toLocaleString()}. You can use this detail page link as an attachment reference.` : "You can use this detail page link as an attachment reference."}
        />
      ) : null}

      <Row gutter={24}>
        <Col xs={24}>
          <DetailSection title="User Information" icon={<UserOutlined />}>
            <Descriptions column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label="Employee ID">{data.employee_id || "-"}</Descriptions.Item>
              <Descriptions.Item label="Fullname">{data.fullname || "-"}</Descriptions.Item>
              <Descriptions.Item label="Level">{data.level || "-"}</Descriptions.Item>
              <Descriptions.Item label="Department">{data.department || "-"}</Descriptions.Item>
            </Descriptions>
          </DetailSection>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col xs={24}>
          <DetailSection title="Request Details" icon={<FileTextOutlined />}>
            <Descriptions column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label="Record ID">
                {data.record_id ? (
                  <Space size={8}>
                    <Text>{data.record_id}</Text>
                    <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => void handleCopyLink()}>
                      Copy Attachment Link
                    </Button>
                  </Space>
                ) : (
                  "-"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="SAP Reservation ID">{data.sap_reservation_id || "-"}</Descriptions.Item>
              <Descriptions.Item label="Description">{data.description || "-"}</Descriptions.Item>
              <Descriptions.Item label="Approval Status">
                <Tag color={getStatusColor(data.approval_status)}>{data.approval_status || "-"}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Created At">{data.created_at ? new Date(data.created_at).toLocaleString() : "-"}</Descriptions.Item>
            </Descriptions>
          </DetailSection>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col xs={24}>
          <DetailSection title="Reservation Data" icon={<TableOutlined />}>
            <ReservationItemsTable items={data.detail || []} />
          </DetailSection>
        </Col>
      </Row>

      {data.approval_process_id ? (
        <Row gutter={24}>
          <Col xs={24}>
            <DetailSection title="Approval Info & Comments" icon={<FileTextOutlined />}>
              <ApprovalInfo documentData={data} usePanel={false} />
            </DetailSection>
          </Col>
        </Row>
      ) : null}
    </>
  )
}

export default SapReservationDetailView
